import { createFileRoute, useParams } from '@tanstack/react-router';
import {
  Alert,
  AccountCard,
  EmptyState,
  Button,
  ibanToBbanCz,
  Spinner,
} from '@penta/penta-shared';
import styles from '../../dashboard.module.scss';
import { useTranslation } from 'react-i18next';
import { usePentaRq } from '@penta/penta-api';
import { useCallback } from 'react';
import { useClient } from '../../../contexts/ClientContext';
import { MessageList } from '../../../components/Messages/MessageList';
import { useRedirectToOldIb } from '../../../hooks/useRedirectToOldIb';

export const Route = createFileRoute('/$locale/smartbanking/dashboard')({
  component: Overview,
});

export function Overview() {
  const { t, i18n } = useTranslation();
  useParams({ from: '/$locale/smartbanking/dashboard' });
  const api = usePentaRq();
  const { selectedClientId } = useClient();
  const { redirect: redirectToOldIb } = useRedirectToOldIb();

  // Fetch product data (accounts, cards, etc.)
  const {
    data: productData,
    isPending,
    error,
  } = api.useQuery(
    'get',
    '/product',
    {
      params: {
        query: {
          clientId: selectedClientId ?? -1,
        },
      },
    },
    { enabled: selectedClientId !== null },
  );

  const {
    data: portfolioData,
    isPending: isPortfolioPending,
    error: portfolioError,
  } = api.useQuery(
    'get',
    '/portfolio/overview/{clientId}',
    {
      params: {
        path: {
          clientId: selectedClientId ?? -1,
        },
      },
    },
    { enabled: selectedClientId !== null },
  );

  // Helper function to format currency with proper locale
  const formatCurrency = useCallback(
    (amount: number, currency: string) => {
      return new Intl.NumberFormat(i18n.language, {
        style: 'currency',
        currency,
      }).format(amount);
    },
    [i18n.language],
  );

  const formatAccountNumber = useCallback((iban: string) => {
    const bbanResult = ibanToBbanCz(iban);
    return bbanResult.isValid ? bbanResult.formattedBban : iban;
  }, []);

  const getCurrentAccountActions = useCallback(
    (accountId: string | number) => [
      {
        'data-testid': `btn-detail-${accountId}`,
        label: t('accounts.detail'),
        variant: 'outline' as const,
        onClick: () => redirectToOldIb('AccountList'),
      },
      {
        'data-testid': `btn-pay-${accountId}`,
        label: t('accounts.pay'),
        variant: 'outline' as const,
        onClick: () => redirectToOldIb('SepaPaymentOrder'),
      },
      {
        'data-testid': `btn-history-${accountId}`,
        label: t('accounts.history'),
        variant: 'outline' as const,
        onClick: () => redirectToOldIb('TurnoverList'),
      },
      {
        'data-testid': `btn-orders-${accountId}`,
        label: t('accounts.standingOrders'),
        variant: 'outline' as const,
        onClick: () => redirectToOldIb('StandingOrderList'),
      },
    ],
    [t, redirectToOldIb],
  );

  const getTermAccountActions = useCallback(
    (accountId: string | number) => [
      {
        'data-testid': `btn-detail-${accountId}`,
        label: t('accounts.detail'),
        variant: 'outline' as const,
        onClick: () => redirectToOldIb('TimeDepositTurnList'),
      },
      {
        'data-testid': `btn-statement-${accountId}`,
        label: t('accounts.statement'),
        variant: 'outline' as const,
        onClick: () => redirectToOldIb('StatementList'),
      },
      {
        'data-testid': `btn-history-${accountId}`,
        label: t('accounts.history'),
        variant: 'outline' as const,
        onClick: () => redirectToOldIb('TurnoverList'),
      },
    ],
    [t, redirectToOldIb],
  );

  const getCreditAccountActions = useCallback(
    (accountId: string | number) => [
      {
        'data-testid': `btn-detail-${accountId}`,
        label: t('accounts.detail'),
        variant: 'outline' as const,
        onClick: () => redirectToOldIb('AccountLoanList'),
      },
    ],
    [t, redirectToOldIb],
  );

  const getPortfolioActions = useCallback(
    (portfolioId?: number, portfolioType?: string) => {
      if (!portfolioId) return [];

      let redirectCode = 'PortfolioOverview';
      if (portfolioType === 'CBR') redirectCode = 'PortfolioBRC';
      else if (portfolioType === 'PWM') redirectCode = 'PortfolioPWMStructure';
      else if (portfolioType === 'PPI') redirectCode = 'PortfolioPPI';

      return [
        {
          'data-testid': `btn-detail-${portfolioId}`,
          label: t('accounts.detail'),
          variant: 'outline' as const,
          onClick: () => redirectToOldIb(redirectCode),
        },
      ];
    },
    [t, redirectToOldIb],
  );

  const currentAccounts = productData?.currentAccounts || [];
  const savingsAccounts = productData?.savingsAccounts || [];
  const termAccounts = productData?.termAccounts || [];
  const creditAccounts = productData?.creditAccounts || [];
  const portfolios = portfolioData?.portfolioTypeOverview || [];

  return (
    <div className={styles.dashboardContent}>
      <section>
        <header>
          <h1 className="h2">{t('dashboard.overview')}</h1>
        </header>

        {isPending ? (
          <div className={styles.loadingWrapper}>
            <Spinner size="lg" />
          </div>
        ) : error ? (
          <Alert variant="error" data-testid="error-products">
            {t('dashboard.errorLoading')}
          </Alert>
        ) : (
          <>
            <header>
              <h2 className="h3">{t('dashboard.deposits')}</h2>
            </header>
            <section className={styles.accountGroup}>
              {currentAccounts.map((account) => (
                <AccountCard
                  key={account.id}
                  data-testid={`account-card-current-${account.id}`}
                  title={account.name}
                  description={formatAccountNumber(account.iban)}
                  amount={formatCurrency(
                    account.currentBalance,
                    account.currency,
                  )}
                  unit=""
                  subvalue={`${t('accounts.accountBalance')}: ${formatCurrency(account.currentBalance, account.currency)}`}
                  actions={getCurrentAccountActions(account.id)}
                />
              ))}
            </section>

            <>
              <header>
                <h2 className="h3">{t('dashboard.savingsAccounts')}</h2>
              </header>

              <section className={styles.accountGroup}>
                {savingsAccounts.length === 0 ? (
                  <EmptyState
                    title={t('dashboard.savingsEmpty')}
                    action={
                      <Button
                        variant="outline"
                        onClick={() => redirectToOldIb('AccountList')}
                        data-testid="btn-create-savings"
                      >
                        {t('dashboard.termDepositsCreate')}
                      </Button>
                    }
                    data-testid="savings-empty-state"
                  />
                ) : (
                  savingsAccounts.map((account) => (
                    <AccountCard
                      key={account.id}
                      data-testid={`account-card-savings-${account.id}`}
                      title={account.name}
                      description={formatAccountNumber(account.iban)}
                      amount={formatCurrency(
                        account.currentBalance,
                        account.currency,
                      )}
                      unit=""
                      subvalue={`${t('accounts.accountBalance')}: ${formatCurrency(account.currentBalance, account.currency)}`}
                      actions={getCurrentAccountActions(account.id)}
                    />
                  ))
                )}
              </section>
            </>

            <>
              <header>
                <h2 className="h3">{t('dashboard.termAccounts')}</h2>
              </header>

              <section className={styles.accountGroup}>
                {termAccounts.length === 0 ? (
                  <EmptyState
                    title={t('dashboard.termDepositsEmpty')}
                    action={
                      <Button
                        variant="outline"
                        onClick={() => redirectToOldIb('TimeDepositNew')}
                        data-testid="btn-create-term-deposit"
                      >
                        {t('dashboard.termDepositsCreate')}
                      </Button>
                    }
                    data-testid="term-deposits-empty-state"
                  />
                ) : (
                  termAccounts.map((account) => (
                    <AccountCard
                      key={account.id}
                      data-testid={`account-card-term-${account.id}`}
                      title={account.name}
                      description={formatAccountNumber(account.iban)}
                      amount={formatCurrency(
                        account.currentBalance,
                        account.currency,
                      )}
                      unit=""
                      subvalue={`${t('accounts.accountBalance')}: ${formatCurrency(account.currentBalance, account.currency)}`}
                      actions={getTermAccountActions(account.id)}
                    />
                  ))
                )}
              </section>
            </>

            <>
              <header>
                <h2 className="h3">{t('dashboard.creditAccounts')}</h2>
              </header>

              <section className={styles.accountGroup}>
                {creditAccounts.length === 0 ? (
                  <EmptyState
                    title={t('dashboard.creditEmpty')}
                    action={
                      <Button
                        variant="outline"
                        onClick={() => redirectToOldIb('AccountLoanList')}
                        data-testid="btn-create-credit"
                      >
                        {t('dashboard.termDepositsCreate')}
                      </Button>
                    }
                    data-testid="credit-empty-state"
                  />
                ) : (
                  creditAccounts.map((account) => (
                    <AccountCard
                      key={account.id}
                      data-testid={`account-card-credit-${account.id}`}
                      title={account.name}
                      description={formatAccountNumber(account.iban)}
                      amount={formatCurrency(
                        account.currentBalance,
                        account.currency,
                      )}
                      unit=""
                      subvalue={`${t('accounts.accountBalance')}: ${formatCurrency(account.currentBalance, account.currency)}`}
                      actions={getCreditAccountActions(account.id)}
                    />
                  ))
                )}
              </section>
            </>
          </>
        )}

        <header>
          <h2 className="h3">{t('dashboard.portfolio', 'Portfolio')}</h2>
        </header>

        {isPortfolioPending ? (
          <div className={styles.loadingWrapper}>
            <Spinner size="lg" />
          </div>
        ) : portfolioError ? (
          <Alert variant="error" data-testid="error-portfolio">
            {t('dashboard.errorLoading')}
          </Alert>
        ) : (
          <section className={styles.accountGroup}>
            {portfolios.length === 0 ? (
              <EmptyState
                title={t('dashboard.portfolioEmpty')}
                action={
                  <Button
                    variant="outline"
                    onClick={() => redirectToOldIb('PortfolioOverview')}
                    data-testid="btn-create-portfolio"
                  >
                    {t('dashboard.termDepositsCreate')}
                  </Button>
                }
                data-testid="portfolio-empty-state"
              />
            ) : (
              portfolios.map((item, index) => (
                <AccountCard
                  key={item.portfolioId || index}
                  data-testid={`account-card-portfolio-${item.portfolioId || index}`}
                  title={item.name || ''}
                  description={item.portfolioType || ''}
                  amount={formatCurrency(item.amount || 0, item.currency)}
                  unit=""
                  subvalue={`${t('accounts.accountBalance')}: ${formatCurrency(item.amount || 0, item.currency)}`}
                  actions={getPortfolioActions(
                    item.portfolioId,
                    item.portfolioType,
                  )}
                />
              ))
            )}
          </section>
        )}
      </section>

      <aside aria-label={t('dashboard.news')}>
        <h2 className="h3">{t('dashboard.news')}</h2>
        <MessageList />
      </aside>
    </div>
  );
}
